// Mock data for Nucleus landing page

export const integrations = [
    { id: "jira", name: "Jira", icon: "📊", connected: true, color: "#0052CC" },
    {
        id: "github",
        name: "GitHub",
        icon: "🐙",
        connected: true,
        color: "#181717",
    },
    {
        id: "slack",
        name: "Slack",
        icon: "💬",
        connected: true,
        color: "#4A154B",
    },
    {
        id: "gmail",
        name: "Gmail",
        icon: "📧",
        connected: false,
        color: "#EA4335",
    },
    {
        id: "calendar",
        name: "Calendar",
        icon: "📅",
        connected: false,
        color: "#4285F4",
    },
    {
        id: "whatsapp",
        name: "WhatsApp",
        icon: "📱",
        connected: false,
        color: "#25D366",
    },
];

export const sampleQuestions = [
    {
        id: 1,
        question: "What shipped last week?",
        answer: "Last week your team shipped 3 major features:\n\n1. **Authentication Refactor** (73% complete)\n   - 12 PRs merged\n   - 450+ lines of code\n   - On track for Friday release\n\n2. **Payment Integration**\n   - Stripe API integrated\n   - 8 commits by Sarah Chen\n   - Deployed to staging\n\n3. **Dashboard Performance**\n   - Load time reduced by 40%\n   - 5 PRs merged\n   - Beta testing in progress\n\n**Top Risk:** 3 dependency PRs in review queue",
        timeToAnswer: "Generated in 2.3s",
    },
    {
        id: 2,
        question: "Why are we behind schedule?",
        answer: "Sprint velocity analysis reveals:\n\n**Primary Blockers:**\n- P0 incident consumed 18 hours of sprint capacity\n- 2 team members on PTO (unexpected)\n- 4 PRs waiting for code review (avg wait: 2.1 days)\n\n**Impact:**\n- Original sprint: 45 story points\n- Actual completed: 32 story points\n- Velocity drop: 29%\n\n**Recommendation:** Redistribute 3 P1 tickets to next sprint",
        timeToAnswer: "Generated in 1.8s",
    },
    {
        id: 3,
        question: "Who should build the payment integration?",
        answer: "**Recommended Developer: Sarah Chen**\n\n**Match Score: 94%**\n\nWhy Sarah:\n- Built 3 similar payment features (Stripe, PayPal)\n- Highest completion rate on backend tasks (89%)\n- Available: 18 hours this sprint\n- Past payment work: 0 bugs in production\n\n**Alternative:** Mike Rodriguez (Match Score: 81%)\n- Experience with financial APIs\n- Currently at 70% capacity",
        timeToAnswer: "Generated in 1.5s",
    },
    {
        id: 4,
        question: "What's our tech debt priority?",
        answer: "**Tech Debt Urgency Matrix**\n\n🔴 **Critical (Do Now):**\n- Legacy auth system (Security risk, affects 100% users)\n- Database query optimization (Response time: 3.2s → target: 0.5s)\n\n🟡 **High Priority:**\n- API rate limiting (Current: none, Risk: moderate)\n- Test coverage gaps (Current: 62%, Target: 80%)\n\n🟢 **Medium Priority:**\n- Code documentation\n- Refactor user service\n\n**ROI Analysis:** Fixing critical items = 25 hours saved/week",
        timeToAnswer: "Generated in 2.1s",
    },
];

export const features = [
    {
        title: "Auto-Generated Release Notes",
        description:
            "Technical and non-technical versions in seconds. No more manual compilation.",
        icon: "FileText",
    },
    {
        title: "Incident Timeline Analysis",
        description:
            "Understand what happened, why, and the impact on your team's velocity.",
        icon: "AlertCircle",
    },
    {
        title: "Developer Recommendations",
        description:
            "AI matches tasks to the right engineers based on past work and expertise.",
        icon: "Users",
    },
    {
        title: "Tech Debt Prioritization",
        description:
            "Urgency matrix comparing debt vs. new features with ROI analysis.",
        icon: "TrendingUp",
    },
    {
        title: "Sprint Intelligence",
        description: "Understand blockers, risks, and capacity in real-time.",
        icon: "Target",
    },
    {
        title: "Context-Aware Answers",
        description:
            "Reads commits, PRs, tickets, and messages to understand the full picture.",
        icon: "Brain",
    },
];

export const pricingPlans = [
    {
        name: "Starter",
        price: "$149",
        period: "/month",
        monthlyPrice: 149,
        annualPrice: "$1,490",
        annualPerMonth: "$124/mo",
        annualSavings: "17% off",
        targetCustomer: "10-30 engineers",
        description: "Perfect for small engineering teams",
        features: [
            "Up to 30 team members",
            "3 integrations (Jira, GitHub, Slack)",
            "100 AI queries per month",
            "30-day data retention",
            "Email support",
        ],
        cta: "Start Free Trial",
        popular: false,
    },
    {
        name: "Professional",
        price: "$349",
        period: "/month",
        monthlyPrice: 349,
        annualPrice: "$3,490",
        annualPerMonth: "$291/mo",
        annualSavings: "17% off",
        targetCustomer: "30-100 engineers",
        description: "For growing engineering organizations",
        features: [
            "Up to 100 team members",
            "All 6 integrations included",
            "Unlimited AI queries",
            "60-day data retention",
            "Priority support",
            "Custom reports",
            "API access",
        ],
        cta: "Book a Demo",
        popular: true,
    },
    {
        name: "Enterprise",
        price: "Custom",
        period: "",
        monthlyPrice: null,
        annualPrice: "Custom",
        annualPerMonth: "",
        annualSavings: "Negotiable",
        targetCustomer: "100+ engineers",
        description: "For large engineering organizations",
        features: [
            "Unlimited team members",
            "All integrations + custom connectors",
            "Unlimited AI queries",
            "Unlimited data retention",
            "Dedicated success manager",
            "Custom AI training",
            "SLA guarantee",
            "SSO & advanced security",
        ],
        cta: "Contact Sales",
        popular: false,
    },
];

export const faqs = [
    {
        question: "How long does it take to set up Nucleus?",
        answer: "Most teams are up and running in under 10 minutes. Simply connect your tools (Jira, GitHub, Slack, etc.) via OAuth, and Nucleus immediately starts analyzing your data. No complex configuration or training required.",
    },
    {
        question: "What integrations do you support?",
        answer: "We currently support Jira, GitHub, Slack, Gmail, Google Calendar, and WhatsApp. Enterprise customers can request custom integrations for their proprietary tools. We're constantly adding new integrations based on customer feedback.",
    },
    {
        question: "Is my data secure?",
        answer: "Absolutely. We use bank-level encryption (AES-256) for data at rest and TLS 1.3 for data in transit. We're SOC 2 Type II certified and GDPR compliant. Your data is never used to train public models or shared with third parties. Enterprise customers can opt for dedicated infrastructure.",
    },
    {
        question: "How accurate are the AI-generated insights?",
        answer: "Nucleus achieves 95%+ accuracy on factual queries (what shipped, who worked on what) by directly reading your tools' data. For predictive insights (recommendations, risk analysis), accuracy improves as the system learns your team's patterns. Most customers see actionable insights from day one.",
    },
    {
        question: "Can I try Nucleus before committing?",
        answer: "Yes! We offer a 14-day free trial with full access to all Professional plan features. No credit card required. During the trial, our team will help you get maximum value from Nucleus.",
    },
    {
        question: "How is this different from just using ChatGPT?",
        answer: "ChatGPT doesn't have access to your engineering tools or context. Nucleus connects directly to your Jira, GitHub, Slack, etc., and understands your team's specific workflows, projects, and history. It's like having an AI assistant that's been attending all your standups and reading all your commits for months.",
    },
    {
        question: "What's included in support?",
        answer: "All plans include email support. Professional plans get priority support with 4-hour response time. Enterprise customers receive a dedicated success manager and 1-hour response SLA. We also provide onboarding assistance and training for all customers.",
    },
    {
        question: "Can Nucleus work with our existing workflows?",
        answer: "Yes! Nucleus is designed to augment, not replace, your existing processes. It reads data from your tools but doesn't require you to change how you work. You can query Nucleus via our web app, Slack bot, or API.",
    },
];

export const testimonials = [
    {
        name: "Sarah Chen",
        role: "VP of Engineering",
        company: "TechCorp",
        avatar: "SC",
        quote: "Nucleus saves me 5+ hours every week. What used to take manual spreadsheet work now happens instantly.",
        rating: 5,
    },
    {
        name: "Michael Rodriguez",
        role: "CTO",
        company: "ScaleUp Inc",
        avatar: "MR",
        quote: "Game-changer for our distributed team. Finally, we have visibility without micromanaging.",
        rating: 5,
    },
    {
        name: "Emily Johnson",
        role: "Engineering Manager",
        company: "DataFlow",
        avatar: "EJ",
        quote: "The AI recommendations for task assignments are surprisingly accurate. It's like having a senior EM on staff.",
        rating: 5,
    },
];

export const stats = [
    { value: "6", label: "Hours Saved Weekly", suffix: "+" },
    { value: "95", label: "Accuracy Rate", suffix: "%" },
    { value: "10", label: "Minute Setup", suffix: "" },
    { value: "12", label: "Integrations", suffix: "+" },
];
